import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Heart, 
  Users, 
  Award, 
  Sparkles, 
  Star, 
  Shield,
  Zap,
  Quote,
  ChevronDown,
  Check,
  ArrowRight
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/uploads")) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return url;
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Section Components
const HeroSection = ({ content, styles }) => (
  <motion.section
    initial="hidden"
    animate="visible"
    variants={staggerContainer}
    className="relative min-h-[60vh] flex items-center justify-center overflow-hidden"
    style={{
      paddingTop: `${styles?.padding_top || 80}px`,
      paddingBottom: `${styles?.padding_bottom || 80}px`,
    }}
  >
    {content.background_image && (
      <>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${getImageUrl(content.background_image)})` }}
        />
        <div 
          className="absolute inset-0 bg-[#050505]"
          style={{ opacity: (content.overlay_opacity || 70) / 100 }}
        />
      </>
    )}
    
    <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
      <motion.h1 
        variants={fadeInUp}
        className="text-5xl md:text-6xl lg:text-7xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-6"
      >
        {content.heading}
      </motion.h1>
      {content.subheading && (
        <motion.p 
          variants={fadeInUp}
          className="text-xl md:text-2xl text-[#D4AF37] mb-8"
        >
          {content.subheading}
        </motion.p>
      )}
      {content.text && (
        <motion.div 
          variants={fadeInUp}
          className="text-lg text-[#A1A1AA] max-w-3xl mx-auto leading-relaxed whitespace-pre-line"
        >
          {content.text}
        </motion.div>
      )}
    </div>
  </motion.section>
);

const TextSection = ({ content, styles }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-100px" }}
    variants={staggerContainer}
    className="px-6"
    style={{
      paddingTop: `${styles?.padding_top || 60}px`,
      paddingBottom: `${styles?.padding_bottom || 60}px`,
      backgroundColor: styles?.background_color || "transparent",
    }}
  >
    <div className="max-w-4xl mx-auto">
      {content.title && (
        <motion.h2 
          variants={fadeInUp}
          className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4 text-center"
        >
          {content.title}
        </motion.h2>
      )}
      {content.subtitle && (
        <motion.p 
          variants={fadeInUp}
          className="text-xl text-[#D4AF37] mb-8 text-center"
        >
          {content.subtitle}
        </motion.p>
      )}
      {content.text && (
        <motion.div 
          variants={fadeInUp}
          className="prose prose-lg prose-invert max-w-none text-[#A1A1AA] [&_p]:mb-4 [&_strong]:text-[#F5F5F0] [&_em]:text-[#D4AF37]"
          style={{ textAlign: content.align || 'left' }}
          dangerouslySetInnerHTML={{ __html: content.text }}
        />
      )}
    </div>
  </motion.section>
);

const DividerSection = ({ content }) => (
  <div className="flex justify-center py-8">
    {content.style === 'ornament' ? (
      <div className="flex items-center gap-4">
        <div className="w-16 h-px bg-[#D4AF37]" />
        <div className="w-2 h-2 bg-[#D4AF37] transform rotate-45" />
        <div className="w-16 h-px bg-[#D4AF37]" />
      </div>
    ) : (
      <div 
        style={{ 
          width: content.width || '50%',
          height: `${content.thickness || 1}px`,
          backgroundColor: content.color || '#D4AF37',
        }}
      />
    )}
  </div>
);

const TwoColumnSection = ({ content, styles }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-100px" }}
    variants={staggerContainer}
    className="px-6"
    style={{
      paddingTop: `${styles?.padding_top || 60}px`,
      paddingBottom: `${styles?.padding_bottom || 60}px`,
      backgroundColor: styles?.background_color || "transparent",
    }}
  >
    <div className="max-w-6xl mx-auto">
      <div className={`grid md:grid-cols-2 gap-${content.gap || '12'}`}>
        <motion.div 
          variants={fadeInUp}
          className="prose prose-invert text-[#A1A1AA] [&_h2]:text-[#F5F5F0] [&_h2]:font-['Playfair_Display'] [&_h3]:text-[#F5F5F0] [&_strong]:text-[#D4AF37] [&_p]:text-[#A1A1AA]"
          dangerouslySetInnerHTML={{ __html: content.leftContent || '' }}
        />
        <motion.div 
          variants={fadeInUp}
          className="prose prose-invert text-[#A1A1AA] [&_h2]:text-[#F5F5F0] [&_h3]:text-[#F5F5F0]"
          dangerouslySetInnerHTML={{ __html: content.rightContent || '' }}
        />
      </div>
    </div>
  </motion.section>
);

const ThreeColumnSection = ({ content, styles }) => {
  const cols = content.columns || ['', '', ''];
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className="px-6"
      style={{
        paddingTop: `${styles?.padding_top || 40}px`,
        paddingBottom: `${styles?.padding_bottom || 40}px`,
        backgroundColor: styles?.background_color || "transparent",
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-6">
          {cols.map((col, i) => (
            <motion.div 
              key={i}
              variants={fadeInUp}
              className="[&_h3]:text-[#F5F5F0] [&_h3]:font-['Playfair_Display'] [&_p]:text-[#A1A1AA] [&_span]:text-[#D4AF37]"
              dangerouslySetInnerHTML={{ __html: col }}
            />
          ))}
        </div>
      </div>
    </motion.section>
  );
};

const FeaturesSection = ({ content, styles }) => {
  const features = content.features || [];
  const cols = content.columns || 3;
  
  const iconMap = {
    heart: Heart,
    star: Star,
    shield: Shield,
    zap: Zap,
    users: Users,
    award: Award,
    sparkles: Sparkles
  };

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className="px-6"
      style={{
        paddingTop: `${styles?.padding_top || 40}px`,
        paddingBottom: `${styles?.padding_bottom || 40}px`,
        backgroundColor: styles?.background_color || "transparent",
      }}
    >
      <div className="max-w-6xl mx-auto">
        {content.title && (
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-12 text-center"
          >
            {content.title}
          </motion.h2>
        )}
        <div 
          className="grid gap-8"
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
        >
          {features.map((feature, i) => {
            const IconComp = iconMap[feature.icon] || Sparkles;
            return (
              <motion.div 
                key={i} 
                variants={fadeInUp}
                className="text-center p-8 border border-[rgba(212,175,55,0.1)] hover:border-[rgba(212,175,55,0.3)] transition-colors group"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                  <IconComp className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#A1A1AA] leading-relaxed">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
};

const TestimonialSection = ({ content, styles }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-100px" }}
    variants={fadeInUp}
    className="px-6"
    style={{
      paddingTop: `${styles?.padding_top || 60}px`,
      paddingBottom: `${styles?.padding_bottom || 60}px`,
      backgroundColor: styles?.background_color || "transparent",
    }}
  >
    <div className="max-w-3xl mx-auto text-center">
      <Quote className="w-12 h-12 text-[#D4AF37] mx-auto mb-8 opacity-50" />
      <p className="text-2xl md:text-3xl italic text-[#F5F5F0] mb-8 leading-relaxed font-['Playfair_Display']">
        "{content.quote}"
      </p>
      <div className="flex items-center justify-center gap-4">
        {content.authorImage ? (
          <img 
            src={getImageUrl(content.authorImage)} 
            alt={content.authorName}
            className="w-14 h-14 rounded-full object-cover border-2 border-[#D4AF37]/30"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-[#1A1A1A] flex items-center justify-center border-2 border-[#D4AF37]/30">
            <span className="text-xl text-[#D4AF37] font-semibold">{(content.authorName || "?")[0]}</span>
          </div>
        )}
        <div className="text-left">
          <p className="font-semibold text-[#F5F5F0] text-lg">{content.authorName}</p>
          <p className="text-[#D4AF37]">{content.authorTitle}</p>
        </div>
      </div>
      {content.rating > 0 && (
        <div className="flex justify-center gap-1 mt-6">
          {[...Array(content.rating || 5)].map((_, i) => (
            <Star key={i} className="w-5 h-5 text-[#D4AF37] fill-current" />
          ))}
        </div>
      )}
    </div>
  </motion.section>
);

const CTASection = ({ content, styles }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeInUp}
    className="px-6"
    style={{
      paddingTop: `${styles?.padding_top || 80}px`,
      paddingBottom: `${styles?.padding_bottom || 80}px`,
      backgroundColor: styles?.background_color || "transparent",
    }}
  >
    <div className="max-w-3xl mx-auto text-center">
      {content.heading && (
        <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4">
          {content.heading}
        </h2>
      )}
      {content.subtext && (
        <p className="text-xl text-[#A1A1AA] mb-8">{content.subtext}</p>
      )}
      {content.buttonText && (
        <Link
          to={content.buttonLink || '/contact'}
          className={`inline-flex items-center gap-2 px-10 py-4 font-semibold text-lg transition-all duration-300 ${
            content.buttonStyle === 'outline' 
              ? 'border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505]'
              : 'bg-[#D4AF37] text-[#050505] hover:bg-[#B8962E] hover:scale-105'
          }`}
        >
          {content.buttonText}
          <ArrowRight className="w-5 h-5" />
        </Link>
      )}
    </div>
  </motion.section>
);

// Section Renderer Map
const SECTION_RENDERERS = {
  hero: HeroSection,
  text: TextSection,
  divider: DividerSection,
  two_column: TwoColumnSection,
  three_column: ThreeColumnSection,
  features: FeaturesSection,
  testimonial: TestimonialSection,
  cta: CTASection,
};

const AboutPage = () => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API}/public/page/about`);
        setPage(res.data);
        document.title = `${res.data.title} | Mix & Mingle`;
      } catch (err) {
        console.error("Error fetching about page:", err);
        setError("Failed to load page content");
      } finally {
        setLoading(false);
      }
    };
    
    fetchPage();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#A1A1AA]">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-['Playfair_Display'] text-[#F5F5F0] mb-4">Page Not Found</h1>
          <p className="text-[#A1A1AA] mb-8">The page you're looking for doesn't exist.</p>
          <Link to="/" className="text-[#D4AF37] hover:underline">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {page.sections?.map((section, index) => {
        const Renderer = SECTION_RENDERERS[section.type];
        if (!Renderer) {
          console.warn(`No renderer for section type: ${section.type}`);
          return null;
        }
        return (
          <Renderer 
            key={section.id || index} 
            content={section.content || {}} 
            styles={section.styles || {}}
          />
        );
      })}
    </div>
  );
};

export default AboutPage;
